import React, { useCallback } from 'react'

import { format, isValid } from 'date-fns'
import { isNil } from 'es-toolkit'
import { isEmpty } from 'es-toolkit/compat'
import Link from 'next/link'
import { LuClock } from 'react-icons/lu'
import { twMerge } from 'tailwind-merge'

import { Badge } from '@/components/common/BadgeV2'
import { Skeleton } from '@/components/common/Skeleton'
import { RenderedTextArea } from '@/components/common/TextArea/RenderedTextArea'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/common/TooltipV3'
import { getPersonName } from '@/components/library-v2/people/lib'

import { ensureDateStringIsUTC } from '@/helpers/date'
import {
  parseStoredBoolean,
  parseStoredNumber,
} from '@/helpers/extended-schema'
import { formatDollarAmountWithSuffix } from '@/helpers/string'

import { usePeople } from '@/hooks/library/usePeople'
import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'

import type { PaginatedResponse } from '@/types/Api'
import type {
  EntityCatalogEntry,
  EntityPerson,
  EntityType,
} from '@/types/library/Entity'
import type {
  EntityExtendedJsonSchemaDerivedField,
  ValueFromFieldPotentialUndefinedOrNull,
} from '@/types/library/ExtendedSchema'

// Type guard functions to properly narrow types based on field type
export function getFieldValue<F extends EntityExtendedJsonSchemaDerivedField>(
  entry: EntityCatalogEntry,
  field: F,
): ValueFromFieldPotentialUndefinedOrNull<F> {
  const value = entry.extended_data?.[field.id]
  // Runtime validation would go here, but for type safety we rely on
  // the discriminated union to ensure correct types
  return value as ValueFromFieldPotentialUndefinedOrNull<F>
}

// Generic helpers to remove repetition while keeping strong typing
type PropsWithNullableValue<P extends { value: unknown }> = Omit<P, 'value'> & {
  value: P['value'] | null | undefined
}

// A legacy field once read as text can hold "" where it means no value.
const isBlank = (value: unknown): boolean =>
  isNil(value) || (typeof value === 'string' && value.trim() === '')

const isBlankList = (value: unknown): boolean =>
  isBlank(value) || (Array.isArray(value) && value.length === 0)

// es-toolkit's isEmpty counts every number, even 0, as empty.
const isEmptyUnlessNumber = (value: unknown): boolean =>
  typeof value !== 'number' && isEmpty(value)

// Text the field's type can't read is shown as written; any other value as empty.
const asWritten = (value: unknown): string =>
  typeof value === 'string' ? value : '—'

export function withEmptyFallback<P extends { value: unknown }>(
  Renderer: React.FC<P>,
  isEmpty: (value: P['value']) => boolean = isBlank,
): React.FC<PropsWithNullableValue<P>> {
  const Wrapped: React.FC<PropsWithNullableValue<P>> = (props) => {
    const { value, ...rest } = props

    if (isEmpty(value as P['value'])) {
      return <span className="text-primary-500 dark:text-primary-400">—</span>
    }

    return (
      <Renderer
        {...(rest as Omit<P, 'value'> as P)}
        value={value as P['value']}
      />
    )
  }
  return Wrapped
}

export const ArrayElementBadge: React.FC<
  React.ComponentProps<typeof Badge>
> = ({ children, variant = 'outline', ...props }) => (
  <Badge
    className="max-w-full truncate font-normal text-[13px]"
    variant={variant}
    {...props}
  >
    {children}
  </Badge>
)

interface RendererLayoutProps {
  children: React.ReactNode
  className?: string
}

export const RendererLayout: React.FC<RendererLayoutProps> = ({
  children,
  className,
}) => (
  <div
    className={twMerge(
      'text-sm truncate text-primary-900 dark:text-primary-100',
      className,
    )}
  >
    {children}
  </div>
)

interface StringRendererProps {
  value: string
  className?: string
}

const BaseStringRenderer: React.FC<StringRendererProps> = ({
  value,
  className,
}) => (
  <RendererLayout className={twMerge('text-wrap w-full max-w-full', className)}>
    <RenderedTextArea>{String(value)}</RenderedTextArea>
  </RendererLayout>
)
export const StringRenderer = withEmptyFallback(
  BaseStringRenderer,
  isEmptyUnlessNumber,
)

interface NumberRendererProps {
  value: number | string
  className?: string
}

const BaseNumberRenderer: React.FC<NumberRendererProps> = ({
  value,
  className,
}) => {
  const number = parseStoredNumber(value)
  return (
    <RendererLayout className={twMerge(className)}>
      {number === null ? asWritten(value) : number.toLocaleString()}
    </RendererLayout>
  )
}
export const NumberRenderer = withEmptyFallback(BaseNumberRenderer)

interface BooleanRendererProps {
  value: boolean | string
  className?: string
}

const BaseBooleanRenderer: React.FC<BooleanRendererProps> = ({
  value,
  className,
}) => {
  const answer = parseStoredBoolean(value)
  return (
    <RendererLayout className={twMerge(className)}>
      {answer === null && asWritten(value)}
      {answer === true && <Badge variant="positive">Yes</Badge>}
      {answer === false && <Badge variant="destructive">No</Badge>}
    </RendererLayout>
  )
}
export const BooleanRenderer = withEmptyFallback(BaseBooleanRenderer)

interface BaseListRendererProps {
  value: string[] | number[] | string
  columnId: string
  onClick?: (v: string | number) => void
  className?: string
}

export const BaseListRenderer: React.FC<BaseListRendererProps> = ({
  value,
  columnId,
  onClick,
  className,
}) => {
  const handleClick = useCallback(
    (v: string | number) => (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault()
      onClick?.(v)
    },
    [columnId],
  )

  if (isBlankList(value)) {
    return (
      <RendererLayout className={twMerge(className)}>
        <span className="text-primary-500 dark:text-primary-400">—</span>
      </RendererLayout>
    )
  }

  if (!Array.isArray(value)) {
    return (
      <RendererLayout className={twMerge(className)}>
        {asWritten(value)}
      </RendererLayout>
    )
  }

  return (
    <RendererLayout
      className={twMerge('w-full truncate flex flex-wrap gap-1', className)}
    >
      {value.sort().map((v) => {
        const isString = typeof v === 'string'
        const isClickable = isString && onClick !== undefined
        return (
          <Tooltip key={v} delayDuration={500}>
            <TooltipTrigger asChild={true}>
              <button
                type="button"
                onClick={handleClick(v)}
                className={twMerge(
                  'truncate',
                  'focus:outline-none focus:ring-0',
                  isClickable ? 'cursor-pointer' : 'cursor-default',
                )}
              >
                <ArrayElementBadge
                  key={v}
                  className={twMerge(
                    'text-[13px] max-w-full truncate',
                    isClickable &&
                      'hover:bg-primary-200/70 dark:hover:bg-primary-700/70',
                  )}
                >
                  {typeof v === 'string' ? v : v.toLocaleString()}
                </ArrayElementBadge>
              </button>
            </TooltipTrigger>
            {
              // TODO: implement number filtering
              isClickable && (
                <TooltipContent className="max-w-64">
                  <div className="flex flex-col items-start w-full">
                    <span>{v}</span>
                    <span className="text-xs text-primary-400 dark:text-primary-500">
                      Click to apply as filter
                    </span>
                  </div>
                </TooltipContent>
              )
            }
          </Tooltip>
        )
      })}
    </RendererLayout>
  )
}

export const StringListRenderer = withEmptyFallback(
  BaseListRenderer,
  isBlankList,
)

export const NumberListRenderer = withEmptyFallback(
  BaseListRenderer,
  isBlankList,
)

const dateFormat: Record<'date' | 'datetime', string> = {
  date: 'MM/dd/yyyy',
  datetime: 'MMM do, yyyy h:mm a',
}

interface DateRendererProps {
  value: string
  fmt?: 'date' | 'datetime'
  className?: string
}

const BaseDateRenderer: React.FC<DateRendererProps> = ({
  value,
  fmt = 'datetime',
  className,
}) => {
  // Handle empty or invalid date strings
  if (!value || value.trim() === '') {
    return (
      <RendererLayout className={twMerge(className)}>
        <span className="text-primary-500 dark:text-primary-400">—</span>
      </RendererLayout>
    )
  }

  const dateString = ensureDateStringIsUTC(value)
  const date = new Date(dateString)

  if (!isValid(date)) {
    return (
      <RendererLayout className={twMerge(className)}>{value}</RendererLayout>
    )
  }

  const formatted = format(date, dateFormat[fmt])
  return (
    <RendererLayout
      className={twMerge('truncate flex items-center gap-1.5', className)}
    >
      <LuClock size={14} className="text-primary-500 dark:text-primary-400" />
      <span>{formatted}</span>
    </RendererLayout>
  )
}
export const DateRenderer = withEmptyFallback(
  BaseDateRenderer,
  (v) => isBlank(v) || typeof v !== 'string',
)

interface DropdownRendererProps {
  value: string
  className?: string
}

const BaseDropdownRenderer: React.FC<DropdownRendererProps> = ({
  value,
  className,
}) => <RendererLayout className={twMerge(className)}>{value}</RendererLayout>
export const DropdownRenderer = withEmptyFallback(
  BaseDropdownRenderer,
  isEmptyUnlessNumber,
)

export const MultiSelectRenderer = withEmptyFallback(
  BaseListRenderer,
  isBlankList,
)

interface CurrencyRendererProps {
  value: number | string
  className?: string
}

const BaseCurrencyRenderer: React.FC<CurrencyRendererProps> = ({
  value,
  className,
}) => {
  const amount = parseStoredNumber(value)
  return (
    <RendererLayout className={twMerge('truncate', className)}>
      {amount === null
        ? asWritten(value)
        : formatDollarAmountWithSuffix(amount, 2)}
    </RendererLayout>
  )
}
export const CurrencyRenderer = withEmptyFallback(BaseCurrencyRenderer)

interface FileRendererProps {
  value: string
  className?: string
}

const BaseFileRenderer: React.FC<FileRendererProps> = ({
  value,
  className,
}) => {
  const { activeOrgId } = useActiveOrgId()
  return (
    <RendererLayout className={twMerge('truncate', className)}>
      <Link
        href={`/orgs/${activeOrgId}/library/files/${value}`}
        target="_blank"
        rel="noreferrer"
        className="text-blue-600 dark:text-blue-400 hover:underline underline-offset-2"
      >
        Reference File
      </Link>
    </RendererLayout>
  )
}
export const FileRenderer = withEmptyFallback(BaseFileRenderer)

interface ReferenceRendererProps {
  value: string
  referenceType: EntityType
  className?: string
}

const referenceTypeToLabel: Record<EntityType, string> = {
  company: 'Company',
  person: 'Person',
  agency: 'Agency',
  catalog_entry: 'Catalog Entry',
  department: 'Department',
  file: 'File',
}

interface PersonReferenceRendererProps {
  value: string
}

export const PersonReferenceRenderer: React.FC<
  PersonReferenceRendererProps
> = ({ value }) => {
  const { activeOrgId } = useActiveOrgId()

  const selectPerson = useCallback(
    (data: PaginatedResponse<EntityPerson>) => {
      return data.data.find((p) => p.id === value)
    },
    [value],
  )
  const { data: person, isLoading } = usePeople({
    orgId: activeOrgId,
    select: selectPerson,
  })

  if (isLoading) {
    return <Skeleton className="h-4 w-24" />
  }

  const name = person ? getPersonName(person, null) : 'Person'

  return (
    <RendererLayout className="truncate">
      <Link
        href={`/orgs/${activeOrgId}/library/people/${value}`}
        className="text-blue-600 dark:text-blue-400 w-full truncate hover:underline underline-offset-2"
      >
        {name}
      </Link>
    </RendererLayout>
  )
}

const BaseReferenceRenderer: React.FC<ReferenceRendererProps> = ({
  value,
  referenceType,
  className,
}) => {
  const { activeOrgId } = useActiveOrgId()
  let href = ''
  switch (referenceType) {
    case 'company':
      href = `/orgs/${activeOrgId}/library/companies/${value}`
      break
    case 'person':
      return <PersonReferenceRenderer value={value} />
    case 'agency':
      href = `/orgs/${activeOrgId}/library/agencies/${value}`
      break
    case 'catalog_entry':
      href = `/orgs/${activeOrgId}/library/catalog/entries/${value}`
      break
  }
  return (
    <RendererLayout className={twMerge('truncate', className)}>
      <Link
        href={href}
        target="_blank"
        rel="noreferrer"
        className="text-blue-600 dark:text-blue-400 hover:underline underline-offset-2"
      >
        {referenceTypeToLabel[referenceType]}
      </Link>
    </RendererLayout>
  )
}
export const ReferenceRenderer = withEmptyFallback(BaseReferenceRenderer)

export const renderField = (
  entry: EntityCatalogEntry,
  field: EntityExtendedJsonSchemaDerivedField,
  className?: string,
): JSX.Element | null => {
  const type = field.type

  switch (type) {
    case 'string': {
      const value = getFieldValue(entry, field)
      return <StringRenderer value={value} className={className} />
    }
    case 'number': {
      const value = getFieldValue(entry, field)
      return <NumberRenderer value={value} className={className} />
    }
    case 'boolean': {
      const value = getFieldValue(entry, field)
      return <BooleanRenderer value={value} className={className} />
    }
    case 'string_list': {
      const value = getFieldValue(entry, field)
      return (
        <StringListRenderer
          value={value}
          columnId={field.id}
          className={className}
        />
      )
    }
    case 'number_list': {
      const value = getFieldValue(entry, field)
      return (
        <NumberListRenderer
          value={value}
          columnId={field.id}
          className={className}
        />
      )
    }
    case 'date': {
      const value = getFieldValue(entry, field)
      return <DateRenderer value={value} className={className} />
    }
    case 'dropdown': {
      const value = getFieldValue(entry, field)
      return <DropdownRenderer value={value} className={className} />
    }
    case 'multi_select': {
      const value = getFieldValue(entry, field)
      return (
        <MultiSelectRenderer
          value={value}
          columnId={field.id}
          className={className}
        />
      )
    }
    case 'currency': {
      const value = getFieldValue(entry, field)
      return <CurrencyRenderer value={value} className={className} />
    }
    default: {
      const exhaustiveCheck: never = type
      return null
    }
  }
}
